// services/driver.service.js
import Axios from './api.service';
import { toast } from 'react-toastify';

class DriverService {
  
  // ============================================================================
  // 🚚 GESTION DU PROFIL LIVREUR
  // ============================================================================

  /**
   * Récupère mon profil de livreur
   * @returns {Promise} Profil du livreur
   */
  async getMyProfile() {
    try {
      console.log('👤 Récupération profil livreur');
      
      const response = await Axios.get('/api/driver/me');
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Erreur récupération profil');
      }
      
      console.log('✅ Profil livreur récupéré');
      return response.data.data;
      
    } catch (error) {
      console.error('❌ Erreur récupération profil:', error);
      const message = error.response?.data?.message || 'Erreur lors de la récupération du profil';
      toast.error(message);
      throw error;
    }
  }

  /**
   * Met à jour mon profil de livreur
   * @param {object} profileData - Données du profil
   * @returns {Promise} Profil mis à jour
   */
  async updateMyProfile(profileData) {
    try {
      console.log('✏️ Mise à jour profil livreur');
      
      const response = await Axios.put('/api/driver/me', profileData);
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Erreur mise à jour profil');
      }
      
      console.log('✅ Profil mis à jour');
      toast.success('Profil mis à jour avec succès');
      return response.data.data;
      
    } catch (error) {
      console.error('❌ Erreur mise à jour profil:', error);
      const message = error.response?.data?.message || 'Erreur lors de la mise à jour';
      toast.error(message);
      throw error;
    }
  }

  // ============================================================================
  // 📍 GESTION DE LA DISPONIBILITÉ ET DU STATUT
  // ============================================================================

  /**
   * Met à jour ma disponibilité (en ligne/hors ligne)
   * @param {boolean} isAvailable - Disponible ou non
   * @returns {Promise} Statut mis à jour
   */
  async updateMyAvailability(isAvailable) {
    try {
      console.log(`🔄 Changement disponibilité: ${isAvailable ? 'En ligne' : 'Hors ligne'}`);
      
      const response = await Axios.put('/api/driver/me/availability', {
        is_available: isAvailable
      });
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Erreur changement disponibilité');
      }
      
      console.log('✅ Disponibilité mise à jour');
      toast.success(isAvailable ? 'Vous êtes maintenant en ligne' : 'Vous êtes maintenant hors ligne');
      return response.data.data;
      
    } catch (error) {
      console.error('❌ Erreur changement disponibilité:', error);
      const message = error.response?.data?.message || 'Erreur lors du changement de disponibilité';
      toast.error(message);
      throw error;
    }
  }

  /**
   * Met à jour mon statut détaillé
   * @param {string} status - 'offline' | 'online' | 'busy' | 'on_break'
   * @returns {Promise} Statut mis à jour
   */
  async updateMyStatus(status) {
    try {
      console.log(`🔄 Changement statut: ${status}`);
      
      const response = await Axios.put('/api/driver/me/status', { status });
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Erreur changement statut');
      }
      
      console.log('✅ Statut mis à jour');
      const statusLabels = {
        offline: 'Hors ligne',
        online: 'En ligne',
        busy: 'Occupé',
        on_break: 'En pause'
      };
      toast.success(`Statut: ${statusLabels[status] || status}`);
      return response.data.data;
      
    } catch (error) {
      console.error('❌ Erreur changement statut:', error);
      const message = error.response?.data?.message || 'Erreur lors du changement de statut';
      toast.error(message);
      throw error;
    }
  }

  // ============================================================================
  // 📍 GESTION DE LA LOCALISATION GPS
  // ============================================================================

  /**
   * Met à jour ma position GPS
   * @param {object} location - { latitude, longitude }
   * @returns {Promise} Position mise à jour
   */
  async updateMyLocation(location) {
    try {
      // Ne pas logger pour éviter le spam
      const response = await Axios.put('/api/driver/me/location', location);
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Erreur mise à jour position');
      }
      
      return response.data.data;
      
    } catch (error) {
      console.error('❌ Erreur mise à jour position:', error);
      // Ne pas afficher de toast pour éviter le spam lors du tracking GPS
      throw error;
    }
  }

  /**
   * Récupère ma position GPS actuelle
   * @returns {Promise} Position actuelle
   */
  async getMyLocation() {
    try {
      console.log('📍 Récupération position GPS');
      
      const response = await Axios.get('/api/driver/me/location');
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Erreur récupération position');
      }
      
      return response.data.data;
      
    } catch (error) {
      console.error('❌ Erreur récupération position:', error);
      const message = error.response?.data?.message || 'Erreur lors de la récupération de la position';
      toast.error(message);
      throw error;
    }
  }

  // ============================================================================
  // 📊 STATISTIQUES DU LIVREUR
  // ============================================================================

  /**
   * Récupère mes statistiques
   * @param {string} period - Période ('7d', '30d', '90d')
   * @returns {Promise} Statistiques
   */
  async getMyStats(period = '30d') {
    try {
      console.log(`📊 Récupération statistiques: ${period}`);
      
      const response = await Axios.get('/api/driver/me/stats', {
        params: { period }
      });
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Erreur récupération statistiques');
      }
      
      console.log('✅ Statistiques récupérées');
      return response.data.data;
      
    } catch (error) {
      console.error('❌ Erreur récupération statistiques:', error);
      const message = error.response?.data?.message || 'Erreur lors de la récupération des statistiques';
      toast.error(message);
      throw error;
    }
  }

  /**
   * Récupère mes statistiques détaillées
   * @param {string} period - Période ('7d', '30d', '90d', '365d')
   * @returns {Promise} Statistiques détaillées
   */
  async getMyDetailedStats(period = '30d') {
    try {
      console.log(`📊 Récupération statistiques détaillées: ${period}`);
      
      const response = await Axios.get('/api/driver/me/stats/detailed', {
        params: { period }
      });
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Erreur récupération statistiques');
      }
      
      console.log('✅ Statistiques détaillées récupérées');
      return response.data.data;
      
    } catch (error) {
      console.error('❌ Erreur récupération statistiques détaillées:', error);
      const message = error.response?.data?.message || 'Erreur lors de la récupération';
      toast.error(message);
      throw error;
    }
  }

  // ============================================================================
  // 📦 GESTION DES LIVRAISONS
  // ============================================================================

  /**
   * Récupère l'historique de mes livraisons
   * @param {object} filters - Filtres (page, limit, status, start_date, end_date)
   * @returns {Promise} Historique des livraisons
   */
  async getMyDeliveryHistory(filters = {}) {
    try {
      console.log('📦 Récupération historique livraisons');
      
      const response = await Axios.get('/api/driver/me/history', {
        params: filters
      });
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Erreur récupération historique');
      }
      
      console.log('✅ Historique récupéré');
      return response.data.data;
      
    } catch (error) {
      console.error('❌ Erreur récupération historique:', error);
      const message = error.response?.data?.message || 'Erreur lors de la récupération de l\'historique';
      toast.error(message);
      throw error;
    }
  }

  /**
   * Récupère les détails d'une livraison spécifique
   * @param {string} shippingId - ID de la livraison
   * @returns {Promise} Détails de la livraison
   */
  async getMyDeliveryDetails(shippingId) {
    try {
      console.log(`📦 Récupération détails livraison: ${shippingId}`);
      
      const response = await Axios.get(`/api/driver/me/history/${shippingId}`);
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Erreur récupération détails');
      }
      
      console.log('✅ Détails récupérés');
      return response.data.data;
      
    } catch (error) {
      console.error('❌ Erreur récupération détails:', error);
      const message = error.response?.data?.message || 'Erreur lors de la récupération';
      toast.error(message);
      throw error;
    }
  }

  /**
   * Récupère mes livraisons en cours
   * @returns {Promise} Livraisons en cours
   */
  async getMyCurrentDeliveries() {
    try {
      console.log('📦 Récupération livraisons en cours');
      
      const response = await Axios.get('/api/driver/me/deliveries/current');
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Erreur récupération livraisons');
      }
      
      console.log('✅ Livraisons en cours récupérées');
      return response.data.data;
      
    } catch (error) {
      console.error('❌ Erreur récupération livraisons en cours:', error);
      const message = error.response?.data?.message || 'Erreur lors de la récupération';
      toast.error(message);
      throw error;
    }
  }

  /**
   * Récupère les livraisons disponibles (à prendre)
   * @returns {Promise} Livraisons disponibles
   */
  async getAvailableDeliveries() {
    try {
      console.log('📦 Récupération livraisons disponibles');
      
      const response = await Axios.get('/api/driver/me/deliveries/available');
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Erreur récupération livraisons');
      }
      
      console.log('✅ Livraisons disponibles récupérées');
      return response.data.data;
      
    } catch (error) {
      console.error('❌ Erreur récupération livraisons disponibles:', error);
      const message = error.response?.data?.message || 'Erreur lors de la récupération';
      toast.error(message);
      throw error;
    }
  }

  /**
   * Accepte/Réclame une livraison disponible
   * @param {string} deliveryId - ID de la livraison
   * @returns {Promise} Livraison acceptée
   */
  async acceptDelivery(deliveryId) {
    try {
      console.log(`📦 Acceptation livraison: ${deliveryId}`);

      // Route réelle définie côté backend dans shippingRoutes.js — les
      // actions driver sur une livraison (claim/complete/fail) vivent sous
      // /api/shippings/deliveries/:id/..., pas sous /api/driver/me/...
      const response = await Axios.post(`/api/shippings/deliveries/${deliveryId}/claim`);
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Erreur acceptation livraison');
      }
      
      console.log('✅ Livraison acceptée');
      toast.success('Mission acceptée avec succès !');
      return response.data.data;
      
    } catch (error) {
      console.error('❌ Erreur acceptation livraison:', error);
      const message = error.response?.data?.message || 'Erreur lors de l\'acceptation';
      toast.error(message);
      throw error;
    }
  }

  /**
   * Met à jour le statut d'une livraison.
   *
   * ⚠️ Le backend n'expose PAS de route générique de changement de statut —
   * seulement deux actions dédiées : /complete (livraison réussie) et
   * /fail (échec, avec raison obligatoire). 'out_for_delivery' n'a pas
   * d'action correspondante : claim() y place déjà la mission directement,
   * il n'y a pas d'étape "démarrer la livraison" séparée côté backend.
   *
   * @param {string} deliveryId - ID de la livraison
   * @param {string} status - Nouveau statut ('delivered' | 'failed_delivery')
   * @param {string} reason - Raison de l'échec (obligatoire si status === 'failed_delivery')
   * @param {string} confirmationToken - Contenu du QR code scanné chez le client
   *   (obligatoire côté backend si status === 'delivered' — preuve de remise)
   * @returns {Promise} Livraison mise à jour
   */
  async updateDeliveryStatus(deliveryId, status, reason = null, confirmationToken = null) {
    try {
      console.log(`📦 Mise à jour statut livraison ${deliveryId}: ${status}`);

      let response;
      if (status === 'delivered') {
        if (!confirmationToken) {
          throw new Error('Le scan du QR code du client est obligatoire pour confirmer la livraison');
        }
        response = await Axios.post(`/api/shippings/deliveries/${deliveryId}/complete`, {
          notes: reason || undefined,
          confirmation_token: confirmationToken
        });
      } else if (status === 'failed_delivery') {
        if (!reason) throw new Error('Une raison est requise pour signaler un échec');
        response = await Axios.post(`/api/shippings/deliveries/${deliveryId}/fail`, { reason });
      } else {
        throw new Error(`Statut "${status}" non pris en charge par l'API livreur`);
      }

      if (!response.data.success) {
        throw new Error(response.data.message || 'Erreur mise à jour statut');
      }

      console.log('✅ Statut livraison mis à jour');
      const statusLabels = {
        picked_up: 'Récupérée',
        in_transit: 'En transit',
        out_for_delivery: 'En cours de livraison',
        delivered: 'Livrée',
        failed_delivery: 'Échec de livraison',
        returned: 'Retournée'
      };
      toast.success(`Statut: ${statusLabels[status] || status}`);
      return response.data.data;

    } catch (error) {
      console.error('❌ Erreur mise à jour statut:', error);
      const message = error.response?.data?.message || 'Erreur lors de la mise à jour';
      toast.error(message);
      throw error;
    }
  }

  // ============================================================================
  // 💰 GESTION DES GAINS
  // ============================================================================

  /**
   * Récupère mes gains
   * @param {string} period - Période ('7d', '30d', '90d', '365d')
   * @returns {Promise} Gains
   */
  async getMyEarnings(period = '30d') {
    try {
      console.log(`💰 Récupération gains: ${period}`);
      
      const response = await Axios.get('/api/driver/me/earnings', {
        params: { period }
      });
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Erreur récupération gains');
      }
      
      console.log('✅ Gains récupérés');
      return response.data.data;
      
    } catch (error) {
      console.error('❌ Erreur récupération gains:', error);
      const message = error.response?.data?.message || 'Erreur lors de la récupération des gains';
      toast.error(message);
      throw error;
    }
  }

  /**
   * Récupère mes gains détaillés
   * @param {object} params - { start_date, end_date, group_by }
   * @returns {Promise} Gains détaillés
   */
  async getMyDetailedEarnings(params) {
    try {
      console.log('💰 Récupération gains détaillés');
      
      const response = await Axios.get('/api/driver/me/earnings/detailed', {
        params
      });
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Erreur récupération gains détaillés');
      }
      
      console.log('✅ Gains détaillés récupérés');
      return response.data.data;
      
    } catch (error) {
      console.error('❌ Erreur récupération gains détaillés:', error);
      const message = error.response?.data?.message || 'Erreur lors de la récupération';
      toast.error(message);
      throw error;
    }
  }

  // ============================================================================
  // 🔐 ROUTES ADMIN - GESTION DES LIVREURS
  // ============================================================================

  /**
   * Récupère tous les livreurs (Admin)
   * @param {object} filters - Filtres et pagination
   * @returns {Promise} Liste des livreurs
   */
  async getAllDrivers(filters = {}) {
    try {
      console.log('🚚 Récupération tous les livreurs');
      
      const response = await Axios.get('/api/driver', {
        params: filters
      });
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Erreur récupération livreurs');
      }
      
      console.log('✅ Livreurs récupérés');
      return response.data.data;
      
    } catch (error) {
      console.error('❌ Erreur récupération livreurs:', error);
      const message = error.response?.data?.message || 'Erreur lors de la récupération';
      toast.error(message);
      throw error;
    }
  }

  /**
   * Récupère un livreur par ID (Admin)
   * @param {string} driverId - ID du livreur
   * @returns {Promise} Profil du livreur
   */
  async getDriverById(driverId) {
    try {
      console.log(`🚚 Récupération livreur: ${driverId}`);
      
      const response = await Axios.get(`/api/driver/${driverId}`);
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Erreur récupération livreur');
      }
      
      console.log('✅ Livreur récupéré');
      return response.data.data;
      
    } catch (error) {
      console.error('❌ Erreur récupération livreur:', error);
      const message = error.response?.data?.message || 'Livreur non trouvé';
      toast.error(message);
      throw error;
    }
  }

  /**
   * Récupère les statistiques globales des livreurs (Admin)
   * @param {string} period - Période ('7d', '30d', '90d')
   * @returns {Promise} Vue d'ensemble
   */
  async getDriversOverview(period = '30d') {
    try {
      console.log(`📊 Récupération vue d'ensemble livreurs: ${period}`);
      
      const response = await Axios.get('/api/driver/stats/overview', {
        params: { period }
      });
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Erreur récupération statistiques');
      }
      
      console.log('✅ Vue d\'ensemble récupérée');
      return response.data.data;
      
    } catch (error) {
      console.error('❌ Erreur récupération vue d\'ensemble:', error);
      const message = error.response?.data?.message || 'Erreur lors de la récupération';
      toast.error(message);
      throw error;
    }
  }

  /**
   * Récupère les livreurs disponibles (Admin)
   * @param {object} filters - Filtres (delivery_zone, vehicle_type, min_rating)
   * @returns {Promise} Livreurs disponibles
   */
  async getAvailableDriversAdmin(filters = {}) {
    try {
      console.log('🚚 Récupération livreurs disponibles (Admin)');
      
      const response = await Axios.get('/api/driver/available', {
        params: filters
      });
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Erreur récupération livreurs disponibles');
      }
      
      console.log('✅ Livreurs disponibles récupérés');
      return response.data.data;
      
    } catch (error) {
      console.error('❌ Erreur récupération livreurs disponibles:', error);
      const message = error.response?.data?.message || 'Erreur lors de la récupération';
      toast.error(message);
      throw error;
    }
  }
}

// Export singleton
export default new DriverService();